import type { GraphQLError } from 'graphql';
import { ApolloError } from 'apollo-client';

import type { Mark } from '@atlaskit/editor-prosemirror/model';

import { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { COMMENT_BATCH_SIZE } from '@confluence/comments-data/entry-points/useActiveInlineCommentsQuery.ts';
import {
	CommentActionType,
	CommentType,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { getValidDate } from '@confluence/comments-util/entry-points/dateUtils.ts';
import type {
	AnnotationStatus,
	CommentData as LegacyCommentData,
	CommentsDataMap as LegacyCommentsDataMap,
	RemovedThreadsCommentTypeMap,
	ReplyData as LegacyReplyData,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type {
	CommentData as NewCommentData,
	CommentsDataMap as NewCommentsDataMap,
	ReplyData as NewReplyData,
} from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import type {
	CommentInlineLocation,
	CommentFooterLocation,
	ActiveCommentInlineResolveProperties,
} from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery.ts';
import type { CommentInlineResolveProperties } from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';

type CommentData = LegacyCommentData | NewCommentData;

import { SortValues } from './useCommentsPanel.ts';
import type { SitePermissionType } from '@confluence/comments-data-manager/entry-points/useAllCommentsDataQueryTypes.ts';

const SCROLLED_COMMENTS_HEADER_GAP_SIZE_IN_PIXELS = 8;
const NUM_HIDDEN_PIXELS_TO_BE_CONSIDERED_OUT_OF_VIEW = 60;

const getTotalOffsetAboveCommentsPanelThreads = (generalCommentsHeaderHeight: number): number => {
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	let objectSidebarPanelHeader = document.querySelector(
		"[data-testid='object-sidebar-panel-header']",
	);
	let additionalOffset = 0;
	if (!objectSidebarPanelHeader) {
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		objectSidebarPanelHeader = document.querySelector(
			"[data-testid='scrolled-object-sidebar-panel-header']",
		);
		additionalOffset = SCROLLED_COMMENTS_HEADER_GAP_SIZE_IN_PIXELS;
	}

	const objectSidebarPanelHeaderHeight =
		objectSidebarPanelHeader?.getBoundingClientRect().height ?? 0;
	const objectHeaderHeight =
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		document.querySelector("[data-testid='object-header-container']")?.getBoundingClientRect()
			.height ?? 0;
	const topNavContainerHeight =
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		document.querySelector("[data-testid='grid-topNav']")?.getBoundingClientRect().height ?? 0;
	const bannerContainerHeight =
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		document.querySelector("[data-testid='banner-container']")?.getBoundingClientRect().height ?? 0;
	const editorControlsOnboardingBannerHeight =
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		document
			.querySelector("[data-testid='editor-controls-onboarding-banner']")
			?.getBoundingClientRect().height ?? 0;
	const announcementBannerHeight =
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		document.querySelector("[id='AkBanner']")?.getBoundingClientRect().height ?? 0;

	return (
		objectSidebarPanelHeaderHeight +
		objectHeaderHeight +
		topNavContainerHeight +
		bannerContainerHeight +
		additionalOffset +
		generalCommentsHeaderHeight +
		editorControlsOnboardingBannerHeight +
		announcementBannerHeight
	);
};

export const shouldScrollCommentThreadToTop = (
	commentThreadElement: Element,
	commentType?: CommentType,
): boolean => {
	const commentThreadRect = commentThreadElement.getBoundingClientRect();
	const generalCommentsHeaderHeight =
		commentType === CommentType.GENERAL
			? // eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
				(document
					.querySelector("[data-testid='comment-section-separator']")
					?.getBoundingClientRect().height ?? 0)
			: 0;
	const totalPaddingAboveCommentThreadView = getTotalOffsetAboveCommentsPanelThreads(
		generalCommentsHeaderHeight,
	);

	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	const objectSidebarPanelFooter = document.querySelector(
		"[data-testid='object-sidebar-panel-footer']",
	);
	const objectSidebarPanelFooterHeight =
		objectSidebarPanelFooter?.getBoundingClientRect().height ?? 0;
	const viewportTop = totalPaddingAboveCommentThreadView;
	const viewportBottom = window.innerHeight - objectSidebarPanelFooterHeight;
	const commentTopInView = commentThreadRect.top >= viewportTop;
	const commentBottomInView = commentThreadRect.bottom <= viewportBottom;
	if (commentTopInView && commentBottomInView) {
		// Comment is 100% in view
		return false;
	} else if (!commentTopInView && commentBottomInView) {
		// Comment is <100% in view and the hidden part is the top part of the comment
		return true;
	} else if (commentTopInView && !commentBottomInView) {
		// Comment is <100% in view and the hidden part is the bottom of the comment
		const hiddenBottomHeight = commentThreadRect.bottom - viewportBottom;
		if (hiddenBottomHeight <= NUM_HIDDEN_PIXELS_TO_BE_CONSIDERED_OUT_OF_VIEW) {
			// The hidden part is <= 60 pixels
			return false;
		} else {
			// The hidden part is > 60 pixels
			return true;
		}
	} else {
		// Comment is <100% in view and the hidden part is the top part of the comment
		return true;
	}
};

// This function is used when we want the entire comment thread to be in view, such as after the user clicks the reply button
export const isCommentThreadReplyOutOfView = (
	commentThreadElement: Element,
	commentType?: CommentType,
): boolean => {
	const commentThreadRect = commentThreadElement.getBoundingClientRect();

	const generalCommentsHeaderHeight =
		commentType === CommentType.GENERAL
			? // eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
				(document
					.querySelector("[data-testid='comment-section-separator']")
					?.getBoundingClientRect().height ?? 0)
			: 0;

	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	const objectSidebarPanelFooter = document.querySelector(
		"[data-testid='object-sidebar-panel-footer']",
	);
	const objectSidebarPanelFooterHeight =
		objectSidebarPanelFooter?.getBoundingClientRect().height ?? 0;

	return (
		// Element is partially out of view below the comments panel viewport
		commentThreadRect.bottom + generalCommentsHeaderHeight >
		window.innerHeight - objectSidebarPanelFooterHeight
	);
};

export enum LocationValue {
	PANEL = 'panel',
	FOOTER = 'footer',
	STANDALONE = 'standalone',
}

export const getAnnotationsToLoad = (
	orderedActiveAnnotationIdList: AnnotationStatus[],
): string[] => {
	return orderedActiveAnnotationIdList
		.slice(0, Math.min(orderedActiveAnnotationIdList.length, COMMENT_BATCH_SIZE))
		.filter((item) => !item.isLoaded) // only get the ones that are not loaded yet
		.map((item) => item.threadKey);
};

const getDanglingComments = ({
	commentsDataMap,
	annotationsSet,
	openCommentThreads,
}: {
	commentsDataMap: LegacyCommentsDataMap | NewCommentsDataMap;
	annotationsSet: Set<string> | undefined;
	openCommentThreads: CommentData[];
}) => {
	// Add dangling comments and handle real-time changes
	for (const threadKey in commentsDataMap.inline) {
		const commentThread = commentsDataMap.inline[threadKey];
		const isDanglingComment = (commentThread?.location as CommentInlineLocation)
			?.inlineResolveProperties?.isDangling;

		if (commentThread?.isOpen) {
			// if the comment is already dangling, we want to add it to the list
			if (isDanglingComment) {
				const markerRef = (commentThread?.location as CommentInlineLocation)?.inlineMarkerRef;

				// comment is no longer dangling if it has a marker ref and is in the annotations set
				if (markerRef && annotationsSet && annotationsSet.has(markerRef)) {
					const inlineLocation = commentThread.location as CommentInlineLocation;
					const updatedCommentThread = {
						...commentThread,
						location: {
							...commentThread.location,
							inlineResolveProperties: {
								...(inlineLocation.inlineResolveProperties as ActiveCommentInlineResolveProperties),
								isDangling: false,
							} as CommentInlineResolveProperties,
						},
					} as CommentData;
					openCommentThreads.push(updatedCommentThread);
				} else {
					openCommentThreads.push(commentThread);
				}

				continue;
			}

			// check for real time dangling comments
			const markerRef = (commentThread?.location as CommentInlineLocation)?.inlineMarkerRef;

			// comment is dangling if it has a marker ref and is not in the annotations set
			// deleted and resolved comments are not in commentsDataMap so we know it's only dangling scenario
			// we do this here so the view will update immediately, but the actual mutation is handled in useDanglingOperations
			if (markerRef && annotationsSet && !annotationsSet.has(markerRef)) {
				const inlineLocation = commentThread.location as CommentInlineLocation;
				const updatedCommentThread = {
					...commentThread,
					location: {
						...commentThread.location,
						inlineResolveProperties: {
							...(inlineLocation.inlineResolveProperties as ActiveCommentInlineResolveProperties),
							isDangling: true,
						} as CommentInlineResolveProperties,
					},
				} as CommentData;
				openCommentThreads.push(updatedCommentThread);
			}
		}
	}
};

// organize a list of open comment threads that we have data for
export const getOpenCommentThreads = ({
	commentsDataMap,
	orderedActiveAnnotationIdList,
	removedThreadsMap,
	showInlineComments,
	showGeneralComments,
	location = LocationValue.PANEL,
}: {
	commentsDataMap: LegacyCommentsDataMap | NewCommentsDataMap;
	orderedActiveAnnotationIdList: AnnotationStatus[];
	removedThreadsMap: RemovedThreadsCommentTypeMap;
	showInlineComments: boolean;
	showGeneralComments: boolean;
	location?: LocationValue;
}): (
	| LegacyCommentData
	| ({
			readonly author: {
				readonly __typename: string;
				readonly accountId?: string | null;
				readonly displayName: string | null;
				readonly permissionType: SitePermissionType | null;
				readonly profilePicture: {
					readonly path: string;
				} | null;
				readonly type: string | null;
			};
			readonly body: {
				readonly value: string;
			};
			readonly container: {
				readonly id: string | null;
			};
			readonly contentStatus: string;
			readonly contributors: ReadonlyArray<{
				readonly __typename: string;
				readonly accountId?: string | null;
				readonly displayName: string | null;
				readonly profilePicture: {
					readonly path: string;
				} | null;
			} | null> | null;
			readonly createdAt: {
				readonly value: string;
			};
			readonly createdAtNonLocalized: string;
			readonly id: string;
			readonly links: {
				readonly editui: string | null;
				readonly webui: string | null;
			};
			readonly location: {
				readonly __typename: string;
				readonly commentResolveProperties?: {
					readonly resolved: boolean;
					readonly resolvedFriendlyDate: string | null;
					readonly resolvedTime: AGG$Long | null;
					readonly resolvedUser: string | null;
				} | null;
				readonly inlineMarkerRef?: string | null;
				readonly inlineResolveProperties?: {
					readonly isDangling: boolean;
					readonly resolved: boolean;
					readonly resolvedFriendlyDate: string | null;
					readonly resolvedTime: AGG$Long | null;
					readonly resolvedUser: string | null;
				} | null;
				readonly inlineText?: string | null;
				readonly type: string;
			};
			readonly parentId: string | null;
			readonly permissions: {
				readonly isEditable: boolean;
				readonly isRemovable: boolean;
				readonly isResolvable: boolean;
			};
			readonly reactionsSummary: {
				readonly ari: string;
				readonly containerAri: string;
				readonly reactionsCount: number;
				readonly reactionsSummaryForEmoji: ReadonlyArray<{
					readonly count: number;
					readonly emojiId: string;
					readonly id: string;
					readonly reacted: boolean;
				} | null>;
			} | null;
			readonly replies: ReadonlyArray<{
				readonly author: {
					readonly __typename: string;
					readonly accountId?: string | null;
					readonly displayName: string | null;
					readonly permissionType: SitePermissionType | null;
					readonly profilePicture: {
						readonly path: string;
					} | null;
				};
				readonly body: {
					readonly value: string;
				};
				readonly contentStatus: string;
				readonly contributors: ReadonlyArray<{
					readonly __typename: string;
					readonly accountId?: string | null;
					readonly displayName: string | null;
					readonly profilePicture: {
						readonly path: string;
					} | null;
				} | null> | null;
				readonly createdAt: {
					readonly value: string;
				};
				readonly createdAtNonLocalized: string;
				readonly id: string;
				readonly links: {
					readonly editui: string | null;
					readonly webui: string | null;
				};
				readonly location: {
					readonly __typename: string;
					readonly commentResolveProperties?: {
						readonly resolved: boolean;
						readonly resolvedFriendlyDate: string | null;
						readonly resolvedTime: AGG$Long | null;
						readonly resolvedUser: string | null;
					} | null;
					readonly inlineMarkerRef?: string | null;
					readonly inlineResolveProperties?: {
						readonly isDangling: boolean;
						readonly resolved: boolean;
						readonly resolvedFriendlyDate: string | null;
						readonly resolvedTime: AGG$Long | null;
						readonly resolvedUser: string | null;
					} | null;
					readonly inlineText?: string | null;
					readonly type: string;
				};
				readonly parentId: string | null;
				readonly permissions: {
					readonly isEditable: boolean;
					readonly isRemovable: boolean;
					readonly isResolvable: boolean;
				};
				readonly reactionsSummary: {
					readonly ari: string;
					readonly containerAri: string;
					readonly reactionsCount: number;
					readonly reactionsSummaryForEmoji: ReadonlyArray<{
						readonly count: number;
						readonly emojiId: string;
						readonly id: string;
						readonly reacted: boolean;
					} | null>;
				} | null;
				readonly version: {
					readonly by: {
						readonly __typename: string;
						readonly displayName: string | null;
					} | null;
					readonly number: number | null;
					readonly when: string | null;
				};
			} | null>;
			readonly version: {
				readonly by: {
					readonly __typename: string;
					readonly accountId?: string | null;
					readonly displayName: string | null;
					readonly type: string | null;
				} | null;
				readonly friendlyWhen: string | null;
				readonly number: number | null;
				readonly when: string | null;
			};
	  } & {
			type: CommentType;
			isOpen: boolean;
			wasRemovedByAnotherUser: boolean;
			replies: NewReplyData[];
	  })
)[] => {
	const openCommentThreads: (LegacyCommentData | NewCommentData)[] = [];
	const annotationsSet = new Set(orderedActiveAnnotationIdList.map((item) => item.threadKey));

	// inline comments
	if (showInlineComments && location === LocationValue.PANEL) {
		const removedAnnotationIds = new Set(Object.values(removedThreadsMap.inline));
		const filteredActiveAnnotationIds = orderedActiveAnnotationIdList.filter(
			(item) => !removedAnnotationIds.has(item.threadKey),
		);
		const totalAnnotationsCount =
			filteredActiveAnnotationIds.length + Object.keys(removedThreadsMap).length;

		let activeIndex = 0; // Track position in filteredActiveAnnotationIds
		for (let i = 0; i < totalAnnotationsCount; i++) {
			let threadKey = '';

			if (removedThreadsMap.inline.hasOwnProperty(i)) {
				threadKey = removedThreadsMap.inline[i];
				const commentThread = commentsDataMap.inline[threadKey];
				if (commentThread) {
					openCommentThreads.push(commentThread);
				}
			} else if (activeIndex < filteredActiveAnnotationIds.length) {
				threadKey = filteredActiveAnnotationIds[activeIndex].threadKey;
				const commentThread = commentsDataMap.inline[threadKey];

				// dangling comments had a marker ref and will be inside commentsDataMap, so we want to ignore these first
				const isDanglingComment = (commentThread?.location as CommentInlineLocation)
					?.inlineResolveProperties?.isDangling;

				// Don't add if it's dangling or might become dangling (has marker ref but not in annotations set)
				const markerRef = (commentThread?.location as CommentInlineLocation)?.inlineMarkerRef;
				const mightBecomeDangling = markerRef && annotationsSet && !annotationsSet.has(markerRef);

				if (commentThread?.isOpen && !isDanglingComment && !mightBecomeDangling) {
					openCommentThreads.push(commentThread);
				}
				activeIndex++;
			}
		}

		getDanglingComments({
			commentsDataMap,
			openCommentThreads,
			annotationsSet,
		});
	}

	// general comments
	if (showGeneralComments || location === LocationValue.FOOTER) {
		// We can just push these in the order we have them because we have no concept of "active"
		const generalComments = Object.values(commentsDataMap.general).filter(
			(generalComment: CommentData) => {
				return (
					generalComment.isOpen ||
					generalComment.wasRemovedByAnotherUser === CommentActionType.DELETE_COMMENT
				);
			},
		);
		openCommentThreads.push(...generalComments);
	}

	return openCommentThreads;
};

export const getResolvedCommentThreads = ({
	commentsDataMap,
	showInlineComments,
	showGeneralComments,
	location = LocationValue.PANEL,
}: {
	commentsDataMap: LegacyCommentsDataMap | NewCommentsDataMap;
	showInlineComments: boolean;
	showGeneralComments: boolean;
	location?: LocationValue;
}): (
	| LegacyCommentData
	| ({
			readonly author: {
				readonly __typename: string;
				readonly accountId?: string | null;
				readonly displayName: string | null;
				readonly permissionType: SitePermissionType | null;
				readonly profilePicture: {
					readonly path: string;
				} | null;
				readonly type: string | null;
			};
			readonly body: {
				readonly value: string;
			};
			readonly container: {
				readonly id: string | null;
			};
			readonly contentStatus: string;
			readonly contributors: ReadonlyArray<{
				readonly __typename: string;
				readonly accountId?: string | null;
				readonly displayName: string | null;
				readonly profilePicture: {
					readonly path: string;
				} | null;
			} | null> | null;
			readonly createdAt: {
				readonly value: string;
			};
			readonly createdAtNonLocalized: string;
			readonly id: string;
			readonly links: {
				readonly editui: string | null;
				readonly webui: string | null;
			};
			readonly location: {
				readonly __typename: string;
				readonly commentResolveProperties?: {
					readonly resolved: boolean;
					readonly resolvedFriendlyDate: string | null;
					readonly resolvedTime: AGG$Long | null;
					readonly resolvedUser: string | null;
				} | null;
				readonly inlineMarkerRef?: string | null;
				readonly inlineResolveProperties?: {
					readonly isDangling: boolean;
					readonly resolved: boolean;
					readonly resolvedFriendlyDate: string | null;
					readonly resolvedTime: AGG$Long | null;
					readonly resolvedUser: string | null;
				} | null;
				readonly inlineText?: string | null;
				readonly type: string;
			};
			readonly parentId: string | null;
			readonly permissions: {
				readonly isEditable: boolean;
				readonly isRemovable: boolean;
				readonly isResolvable: boolean;
			};
			readonly reactionsSummary: {
				readonly ari: string;
				readonly containerAri: string;
				readonly reactionsCount: number;
				readonly reactionsSummaryForEmoji: ReadonlyArray<{
					readonly count: number;
					readonly emojiId: string;
					readonly id: string;
					readonly reacted: boolean;
				} | null>;
			} | null;
			readonly replies: ReadonlyArray<{
				readonly author: {
					readonly __typename: string;
					readonly accountId?: string | null;
					readonly displayName: string | null;
					readonly permissionType: SitePermissionType | null;
					readonly profilePicture: {
						readonly path: string;
					} | null;
				};
				readonly body: {
					readonly value: string;
				};
				readonly contentStatus: string;
				readonly contributors: ReadonlyArray<{
					readonly __typename: string;
					readonly accountId?: string | null;
					readonly displayName: string | null;
					readonly profilePicture: {
						readonly path: string;
					} | null;
				} | null> | null;
				readonly createdAt: {
					readonly value: string;
				};
				readonly createdAtNonLocalized: string;
				readonly id: string;
				readonly links: {
					readonly editui: string | null;
					readonly webui: string | null;
				};
				readonly location: {
					readonly __typename: string;
					readonly commentResolveProperties?: {
						readonly resolved: boolean;
						readonly resolvedFriendlyDate: string | null;
						readonly resolvedTime: AGG$Long | null;
						readonly resolvedUser: string | null;
					} | null;
					readonly inlineMarkerRef?: string | null;
					readonly inlineResolveProperties?: {
						readonly isDangling: boolean;
						readonly resolved: boolean;
						readonly resolvedFriendlyDate: string | null;
						readonly resolvedTime: AGG$Long | null;
						readonly resolvedUser: string | null;
					} | null;
					readonly inlineText?: string | null;
					readonly type: string;
				};
				readonly parentId: string | null;
				readonly permissions: {
					readonly isEditable: boolean;
					readonly isRemovable: boolean;
					readonly isResolvable: boolean;
				};
				readonly reactionsSummary: {
					readonly ari: string;
					readonly containerAri: string;
					readonly reactionsCount: number;
					readonly reactionsSummaryForEmoji: ReadonlyArray<{
						readonly count: number;
						readonly emojiId: string;
						readonly id: string;
						readonly reacted: boolean;
					} | null>;
				} | null;
				readonly version: {
					readonly by: {
						readonly __typename: string;
						readonly displayName: string | null;
					} | null;
					readonly number: number | null;
					readonly when: string | null;
				};
			} | null>;
			readonly version: {
				readonly by: {
					readonly __typename: string;
					readonly accountId?: string | null;
					readonly displayName: string | null;
					readonly type: string | null;
				} | null;
				readonly friendlyWhen: string | null;
				readonly number: number | null;
				readonly when: string | null;
			};
	  } & {
			type: CommentType;
			isOpen: boolean;
			wasRemovedByAnotherUser: boolean;
			replies: NewReplyData[];
	  })
)[] => {
	const resolvedCommentThreads: (LegacyCommentData | NewCommentData)[] = [];
	// inline comments
	if (showInlineComments && location === LocationValue.PANEL) {
		for (const key in commentsDataMap.inline) {
			const comment = commentsDataMap.inline[key];
			if (comment.isOpen === false) {
				resolvedCommentThreads.push(comment);
			}
		}
	}

	// general comments
	if (showGeneralComments) {
		for (const key in commentsDataMap.general) {
			const comment = commentsDataMap.general[key];
			if (comment.isOpen === false) {
				resolvedCommentThreads.push(comment);
			}
		}
	}

	return resolvedCommentThreads;
};

const getCommentResolvedOrCreatedTime = (comment: LegacyCommentData | NewCommentData): number => {
	const commentLocation = comment.location;
	const resolvedTime =
		(commentLocation as CommentInlineLocation)?.inlineResolveProperties?.resolvedTime ||
		(commentLocation as CommentFooterLocation)?.commentResolveProperties?.resolvedTime; // from the BE, this is for both reopened and resolved time, in milliseconds
	return resolvedTime || getValidDate(comment.createdAtNonLocalized).getTime(); // getTime() returns milliseconds
};

export const getSortedCommentThreads = (
	currentSort: SortValues,
	commentThreads: CommentData[],
): CommentData[] => {
	if (currentSort === SortValues.MOST_RECENT) {
		return [...commentThreads].sort(
			(a, b) => getCommentResolvedOrCreatedTime(b) - getCommentResolvedOrCreatedTime(a),
		);
	}

	return commentThreads;
};

export const getAnnotationThreadKeys = (annotations: AnnotationStatus[]): string[] =>
	annotations.map((annotation) => annotation.threadKey);

export const getMarkIds = (marks: Mark[] = []): any[] => marks.map((mark) => mark.attrs.id);

export const getCommentsSortedOldestToNewest = (
	commentA: LegacyCommentData | NewCommentData | LegacyReplyData | NewReplyData,
	commentB: LegacyCommentData | NewCommentData | LegacyReplyData | NewReplyData,
): number =>
	getValidDate(commentA?.createdAtNonLocalized).getTime() -
	getValidDate(commentB?.createdAtNonLocalized).getTime();

// Used to determine if a GQL query error only contains reactionsSummary errors.
// If true, we can ignore the error and still render the comments data that's fetched successfully.
export const onlyHasReactionsSummaryErrors = (error: any): boolean => {
	if (!error) {
		// if falsy, consider it as NOT containing non-reactionsSummary errors
		return true;
	} else if (error instanceof ApolloErrorInterop || error instanceof ApolloError) {
		// TODO we will need to migrate to ApolloRelayInterops to get proper error type of ApolloErrorInterop instead of ApolloError
		// after migration, we can remove the instanceof ApolloError check here
		const hasOtherGqlErrors =
			error.graphQLErrors?.filter((e: GraphQLError) => !e.path?.includes('reactionsSummary'))
				?.length > 0;

		return !hasOtherGqlErrors && !error.networkError;
	} else {
		// if truthy but not an ApolloErrorInterop, consider it as containing non-reactionsSummary errors
		return false;
	}
};
